import { Subject, Observable } from "rxjs";

import { Injectable, inject } from "@angular/core";
import { environment } from "../environments/environment";
import * as $ from 'jquery';
import 'signalr';

export class SignalrWindow extends Window {
  $: any;
}

export enum ConnectionState {
  Connecting = 1,
  Connected = 2,
  Reconnecting = 3,
  Disconnected = 4
}

export class SignalRConfig {
  url: string;
  hubName: string;
  channel: string;
}

export class Message {
  Id: Number;
  Subject: string;
  IsNew: Boolean
  AppId: string
  Text: string;
  Type: any;
  ObjectId: string;
  Order: Number;

  constructor(private m: any) {
    if (m) {
      this.Id = m.id;
      this.IsNew = (m.status === 0);
      this.AppId = m.message.appId;
      this.Text = m.message.text;
      this.Type = m.message.type;
      this.Subject = m.message.subject;
      this.ObjectId = m.message.objectId;
    }
  }
}

@Injectable()
export class SignalRService {
  private window = inject(SignalrWindow);
  private channelConfig = inject<SignalRConfig>("channel.config" as any);

  starting$: Observable<any>;
  connectionState$: Observable<ConnectionState>;
  error$: Observable<string>;

  private messageSubject: Subject<Message>;
  private errorSubject = new Subject<any>();
  private startingSubject = new Subject<any>();
  private connectionStateSubject = new Subject<ConnectionState>();

  private hubProxy: any;
  private hubConnection: any;

  // Reconnect handling
  private reconnectAttempts = 0;
  private reconnectTimer: any = null;
  private onlineListener: any;
  private visibilityListener: any;
  private startOptions: any = { transport: ["webSockets", "serverSentEvents", "longPolling"] };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const channelConfig = this.channelConfig;

    channelConfig.url = `${environment.baseUrl}/signalr`;
    if (this.window.$ === undefined || this.window.$.hubConnection === undefined) {
      throw new Error("The variable '$' or the .hubConnection() function are not defined...please check the SignalR scripts have been loaded properly");
    }

    this.connectionState$ = this.connectionStateSubject.asObservable();
    this.error$ = this.errorSubject.asObservable();
    this.starting$ = this.startingSubject.asObservable();

    this.hubConnection = this.window.$.hubConnection();
    this.hubConnection.url = channelConfig.url;
    this.hubProxy = this.hubConnection.createHubProxy(channelConfig.hubName);

    this.hubConnection.stateChanged((state: any) => {
      let newState = ConnectionState.Connecting;

      switch (state.newState) {
        case this.window.$.signalR.connectionState.connecting:
          newState = ConnectionState.Connecting;
          break;
        case this.window.$.signalR.connectionState.connected:
          newState = ConnectionState.Connected;
          break;
        case this.window.$.signalR.connectionState.reconnecting:
          newState = ConnectionState.Reconnecting;
          break;
        case this.window.$.signalR.connectionState.disconnected:
          newState = ConnectionState.Disconnected;
          break;
      }

      this.connectionStateSubject.next(newState);
    });

    this.hubConnection.error((error: any) => {
      this.errorSubject.next(error);
    });

    // Handle reconnect lifecycle
    this.hubConnection.reconnecting(() => {
      // Browser throttling/background can cause reconnects; just log
      console.log("SignalR: reconnecting...");
    });

    this.hubConnection.reconnected(() => {
      console.log("SignalR: reconnected");
      this.clearReconnectTimer();
      this.reconnectAttempts = 0;
      // Notify subscribers to re-subscribe if needed
      this.startingSubject.next(null);
    });

    this.hubConnection.disconnected(() => {
      console.warn("SignalR: disconnected");
      this.scheduleReconnect("disconnected");
    });

    this.hubProxy.on("onEvent", (message: Message) => {

      return this.messageSubject.next(message);
    });

  }

  start(): void {
    // Avoid duplicate starts when already connecting/connected
    if (this.isConnectingOrConnected()) {
      return;
    }

    this.hubConnection.start(this.startOptions)
      .done(() => {
        console.log(`SignalR started!`);
        this.startingSubject.next(null);
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
      })
      .fail((error: any) => {
        console.error("SignalR start failed:", error);
        // Keep the subject alive; schedule reconnect instead of erroring the stream
        this.scheduleReconnect("start-failed");
      });
  }

  sub(): Observable<any> {
    if (this.messageSubject !== undefined) {
      return this.messageSubject.asObservable();
    }

    this.messageSubject = new Subject<Message>();
    this.starting$.subscribe(() => {
      this.hubProxy.invoke("Subscribe")
        .done(() => {
          console.log(`Connected to SignalR!`);
        })
        .fail((error: any) => {
          this.messageSubject.error(error);
        });

    });

    return this.messageSubject.asObservable();
  }

  publish(ev: Message): void {
    this.hubProxy.invoke("Publish", ev);
  }

  // ----- Private helpers -----
  private isConnectingOrConnected(): boolean {
    const state = this.hubConnection?.state;
    const s = this.window.$?.signalR?.connectionState;
    return s && (state === s.connecting || state === s.connected || state === s.reconnecting);
  }

  private isDisconnected(): boolean {
    const state = this.hubConnection?.state;
    const s = this.window.$?.signalR?.connectionState;
    return s && state === s.disconnected;
  }

  private computeBackoffDelay(): number {
    // 2s, 5s, 10s, 30s, 60s, 60s...
    const steps = [2000, 5000, 10000, 30000, 120000];
    const idx = Math.min(this.reconnectAttempts, steps.length - 1);
    let delay = steps[idx];

    // In background, use longer delay to play nice with throttling
    if (typeof document !== 'undefined' && document.hidden) {
      delay = Math.max(delay, 30000);
    }

    return delay;
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  private scheduleReconnect(reason: string) {
    // Already trying or already connected
    if (!this.isDisconnected()) {
      return;
    }

    this.clearReconnectTimer();

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const attempt = () => {
      if (this.isConnectingOrConnected()) return;
      console.log(`SignalR: attempting reconnect (#${this.reconnectAttempts + 1})`);
      this.reconnectAttempts++;
      this.start();
    };

    if (!online) {
      console.warn("SignalR: offline detected, waiting for network to recover");
      // Try once per minute while offline, and trigger immediately when online
      this.reconnectTimer = setTimeout(() => attempt(), 120000);
      this.onlineListener = () => {
        console.log("SignalR: online event, reconnecting now");
        this.clearReconnectTimer();
        attempt();
      };
      window.addEventListener('online', this.onlineListener);
      return;
    }

    const delay = this.computeBackoffDelay();
    console.warn(`SignalR: scheduling reconnect in ${delay}ms (reason: ${reason})`);
    this.reconnectTimer = setTimeout(() => attempt(), delay);

    // If tab becomes visible before the timer, try sooner
    this.visibilityListener = () => {
      if (!document.hidden && this.isDisconnected()) {
        console.log("SignalR: tab visible, fast-track reconnect");
        this.clearReconnectTimer();
        attempt();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
  }
}
